import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { useIsMobile } from '../hooks/useIsMobile';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Tooltip, Legend, Filler, ArcElement
);

const CHART_COLORS = ['#00f2fe', '#8b5cf6', '#f43f5e', '#fbbf24', '#10b981'];
const AVATAR_COLORS = ['#ec4899', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#00f2fe', '#ef4444', '#a78bfa'];

export default function Dashboard() {
  const token = localStorage.getItem('crm_token');
  const isMobile = useIsMobile();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    conversationsToday: 0,
    pendingAppointments: 0,
    unreadMessages: 0,
    pendingPreconsultations: 0,
    endingPackages: 0,
    paymentsToCollect: 0
  });

  const [todayAppointments, setTodayAppointments] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [unreadMessagesByPlatform, setUnreadMessagesByPlatform] = useState({
    instagram: 0,
    whatsapp: 0,
    messenger: 0,
    tiktok: 0,
    sms: 0
  });
  const [opportunities, setOpportunities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [weeklyConversations, setWeeklyConversations] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [aptRes, callRes, summaryRes, preconsCallbackRes] = await Promise.all([
        axios.get(`/api/appointments?date=${today}`, { headers }).catch(() => ({ data: [] })),
        axios.get('/api/callbacks?status=pending', { headers }).catch(() => ({ data: [] })),
        axios.get('/api/dashboard/summary', { headers }).catch(() => ({ data: null })),
        // Ön Görüşme / Mesajlar'daki "Tekrar Ara" kayıtları ayrı callbacks tablosuna
        // DEĞİL, pre_consultation_requests'e (status='callback_later' + callback_date)
        // yazılıyor - kart eskiden sadece callbacks'i okuduğu için hep boş görünüyordu.
        // Bu uç callback_later kayıtlarını aranma zamanına göre filtreliyor: bugün
        // aranacaklar + günü geçmiş (gecikmiş) olanlar.
        axios.get(`/api/pre-consultations?status=callback_later&date_from=${today}&date_to=${today}&limit=50`, { headers }).catch(() => ({ data: {} }))
      ]);

      const apts = aptRes.data || [];
      const summary = summaryRes?.data || {};

      setTodayAppointments(apts);
      const manualCallbacks = (Array.isArray(callRes.data) ? callRes.data : []).map(c => ({ ...c, key: `cb-${c.id}`, link: '/callbacks' }));
      const preconsCallbacks = (preconsCallbackRes.data?.requests || [])
        .filter(r => r.status === 'callback_later' && r.callback_date)
        .map(r => ({
          key: `pc-${r.id}`,
          customer_name: r.caller_name || 'İsimsiz',
          phone: r.phone,
          callback_date: r.callback_date,
          is_overdue: new Date(String(r.callback_date).replace(' ', 'T')) < new Date(),
          link: '/pre-consultations'
        }))
        .sort((a, b) => String(a.callback_date).localeCompare(String(b.callback_date)));
      // Önce gecikmiş/bugünkü ön görüşme geri aramaları (saat sırasıyla), sonra elle eklenenler.
      setCallbacks([...preconsCallbacks, ...manualCallbacks]);
      setUnreadMessagesByPlatform(summary.unread_messages_by_platform || {
        instagram: 0,
        whatsapp: 0,
        messenger: 0,
        tiktok: 0,
        sms: 0
      });
      setOpportunities(summary.opportunities || []);
      setActivities(summary.recent_activities || []);

      setStats({
        conversationsToday: summary.conversations_today ?? 0,
        pendingAppointments: summary.pending_appointments ?? 0,
        unreadMessages: summary.unread_messages ?? 0,
        pendingPreconsultations: summary.pending_preconsultations ?? 0,
        endingPackages: summary.ending_packages_count ?? 0,
        paymentsToCollect: summary.payments_to_collect_count ?? 0
      });

      if (Array.isArray(summary.weekly_conversations)) {
        setWeeklyConversations(summary.weekly_conversations);
      }
    } catch (e) {
      console.error('Dashboard error:', e);
    } finally {
      setLoading(false);
    }
  };

  const last7DaysLabels = Array.from({ length: 7 }, (_, i) => {
    const d = subDays(new Date(), 6 - i);
    const dayStr = format(d, 'EEEEEE', { locale: tr });
    return dayStr.charAt(0).toUpperCase() + dayStr.slice(1);
  });

  const barData = {
    labels: last7DaysLabels,
    datasets: [{
      label: 'Konuşmalar',
      data: weeklyConversations,
      backgroundColor: '#3b82f6',
      borderRadius: 6,
      borderSkipped: false,
    }]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#9ca3af', font: { family: 'Plus Jakarta Sans', size: 12 } }
      },
      y: {
        grid: { color: 'rgba(255, 255, 255, 0.03)' },
        ticks: { color: '#9ca3af', font: { family: 'Plus Jakarta Sans', size: 12 } }
      }
    }
  };

  const statusLabel = { pending: 'Bekliyor', confirmed: 'Onaylandı', in_progress: 'İşlemde', completed: 'Tamamlandı', cancelled_by_customer: 'İptal', cancelled_by_salon: 'İptal', no_show: 'Gelmedi' };
  const statusColor = { pending: '#fbbf24', confirmed: '#00f2fe', in_progress: '#8b5cf6', completed: '#10b981', cancelled_by_customer: '#f43f5e', cancelled_by_salon: '#f43f5e', no_show: '#6b7280' };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div className="loading-spinner" style={{ width: 40, height: 40, border: '3px solid rgba(59,130,241,0.2)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  return (
    <div className="dashboard-page" style={{ padding: isMobile ? '16px 0' : '24px 0', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? 20 : 32, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: 0 }}>Genel Bakış</h1>
          <p style={{ color: '#9ca3af', marginTop: 4, fontSize: 14 }}>
            {format(new Date(), 'EEEE, d MMMM yyyy', { locale: tr })} &nbsp;·&nbsp; {format(currentTime, 'HH:mm')}
          </p>
        </div>
        <button onClick={fetchAll} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, color: '#60a5fa', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all .2s' }}>
          <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
          Yenile
        </button>
      </div>

      {/* KPI Cards Grid */}
      <div className="kpi-grid">
        {/* Card 1: Bugünkü Konuşmalar */}
        <div className="kpi-card">
          <div className="kpi-left">
            <span className="kpi-label">Bugünkü Konuşmalar</span>
            <span className="kpi-value">{stats.conversationsToday}</span>
          </div>
          <div className="kpi-icon icon-blue">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
        </div>

        {/* Card 2: Bekleyen Randevular */}
        <div className="kpi-card">
          <div className="kpi-left">
            <span className="kpi-label">Bekleyen Randevular</span>
            <span className="kpi-value">{stats.pendingAppointments}</span>
          </div>
          <div className="kpi-icon icon-orange">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
          </div>
        </div>

        {/* Card 3: Okunmamış Mesajlar */}
        <div className="kpi-card">
          <div className="kpi-left">
            <span className="kpi-label">Okunmamış Mesajlar</span>
            <span className="kpi-value">{stats.unreadMessages}</span>
          </div>
          <div className="kpi-icon icon-violet">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
        </div>

        {/* Card 4: Bekleyen Ön Görüşmeler */}
        <div className="kpi-card">
          <div className="kpi-left">
            <span className="kpi-label">Bekleyen Ön Görüşmeler</span>
            <span className="kpi-value">{stats.pendingPreconsultations}</span>
          </div>
          <div className="kpi-icon icon-green">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.97-4.03 9-9 9-1.5 0-2.91-.37-4.15-1.02L3 21l1.02-3.85A8.96 8.96 0 0 1 3 12c0-4.97 4.03-9 9-9s9 4.03 9 9z" />
            </svg>
          </div>
        </div>

        {/* Card 5: Paketi Bitmek Üzere */}
        <div className="kpi-card">
          <div className="kpi-left">
            <span className="kpi-label">Paketi Bitmek Üzere</span>
            <span className="kpi-value">{stats.endingPackages}</span>
          </div>
          <div className="kpi-icon icon-pink">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.24L3 3.24v6.59a2 2 0 0 0 .59 1.41l9.58 9.58a2 2 0 0 0 2.83 0l4.59-4.59a2 2 0 0 0 0-2.82z" />
              <circle cx="8" cy="8" r="1.5" />
            </svg>
          </div>
        </div>

        {/* Card 6: Tahsilat Bekleyen */}
        <div className="kpi-card">
          <div className="kpi-left">
            <span className="kpi-label">Tahsilat Bekleyen</span>
            <span className="kpi-value">{stats.paymentsToCollect}</span>
          </div>
          <div className="kpi-icon icon-teal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20M6 15h4" />
            </svg>
          </div>
        </div>
      </div>

      {/* Details Row: 2 Columns */}
      <div className="details-row">
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Section 1: Yaklaşan Randevular */}
          <div className="details-card">
            <div className="details-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(var(--primary-rgb),0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'var(--primary)' }}>⏰</div>
                <div>
                  <h3 className="details-title">Yaklaşan Randevular</h3>
                  <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>Bugünün programı</p>
                </div>
              </div>
              <span style={{ background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>{todayAppointments.length}</span>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {todayAppointments.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', margin: '15px 0' }}>Bugün için randevu bulunmuyor.</p>
              ) : (
                todayAppointments.slice(0, 4).map(apt => {
                  const initials = `${apt.customer_first_name?.[0] || ''}${apt.customer_last_name?.[0] || ''}`.toUpperCase();
                  const color = AVATAR_COLORS[apt.id % AVATAR_COLORS.length];
                  const time = apt.scheduled_at ? apt.scheduled_at.split('T')[1]?.slice(0, 5) : '--:--';
                  return (
                    <div key={apt.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.02)', borderRadius: 14 }}>
                      <div style={{ width: 50, fontSize: 13, fontWeight: 700, color: '#93c5fd' }}>{time}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>{initials}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{apt.customer_first_name} {apt.customer_last_name}</div>
                          <div style={{ color: '#6b7280', fontSize: 11 }}>{apt.service_name}</div>
                        </div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: `${statusColor[apt.status] || '#6b7280'}15`, color: statusColor[apt.status] || '#6b7280', border: `1px solid ${statusColor[apt.status] || '#6b7280'}25` }}>
                        {statusLabel[apt.status] || apt.status}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            {todayAppointments.length > 4 && (
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Link to="/calendar" style={{ color: '#60a5fa', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Daha Fazla Gör →</Link>
              </div>
            )}
          </div>

          {/* Section 2: Geri Aranacak Kişiler */}
          <div className="details-card">
            <div className="details-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#f59e0b' }}>📞</div>
                <div>
                  <h3 className="details-title">Geri Aranacak Kişiler</h3>
                  <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>Bugün aranması gerekenler ve gecikenler</p>
                </div>
              </div>
              <span style={{ background: '#f59e0b', color: '#fff', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>{callbacks.length}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {callbacks.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', margin: '15px 0' }}>Geri aranacak kişi bulunmuyor.</p>
              ) : (
                callbacks.slice(0, 4).map(call => {
                  const isUrgent = call.is_urgent === 1 || call.is_overdue;
                  const initials = (call.customer_name || 'G').substring(0, 2).toUpperCase();
                  const cbDate = call.callback_date ? new Date(String(call.callback_date).replace(' ', 'T')) : null;
                  const cbLabel = cbDate && !isNaN(cbDate)
                    ? (call.is_overdue
                        ? `Gecikti · ${format(cbDate, 'd MMM HH:mm', { locale: tr })}`
                        : `Bugün ${format(cbDate, 'HH:mm')}`)
                    : null;
                  return (
                    <div key={call.key || call.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: isUrgent ? 'rgba(239, 68, 68, 0.05)' : 'rgba(255,255,255,0.01)', border: isUrgent ? '1px solid rgba(239, 68, 68, 0.15)' : '1px solid rgba(255,255,255,0.02)', borderRadius: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: isUrgent ? '#ef4444' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>{initials}</div>
                        <div>
                          <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{call.customer_name}</div>
                          <div style={{ color: '#6b7280', fontSize: 11 }}>
                            {call.phone}
                            {cbLabel && <span style={{ marginLeft: 8, color: call.is_overdue ? '#f87171' : '#fbbf24', fontWeight: 600 }}>🔁 {cbLabel}</span>}
                          </div>
                        </div>
                      </div>
                      <Link to={call.link || '/callbacks'} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: 12 }}>📞</Link>
                    </div>
                  );
                })
              )}
            </div>
            {callbacks.length > 4 && (
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Link to="/pre-consultations" style={{ color: '#60a5fa', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>+{callbacks.length - 4} kişi daha →</Link>
              </div>
            )}
          </div>

          {/* Section 3: Son Aktiviteler */}
          <div className="details-card">
            <div className="details-header">
              <h3 className="details-title">Son Aktiviteler</h3>
              <span className="details-subtitle">Bugün</span>
            </div>
            <div className="activity-list">
              {activities.slice(0, 4).map((act, index) => {
                const formattedTime = typeof act.time === 'string' && act.time.includes('dk') 
                  ? act.time 
                  : format(new Date(act.time), 'HH:mm', { locale: tr });
                return (
                  <div className="activity-item" key={index}>
                    <span className={`activity-dot dot-${act.type}`} />
                    <span className="activity-text">{act.text}</span>
                    <span className="activity-time">{formattedTime}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Section 4: Haftalık Konuşmalar Chart */}
          <div className="details-card">
            <div className="details-header">
              <h3 className="details-title">Haftalık Konuşmalar</h3>
              <span className="details-subtitle">Son 7 gün</span>
            </div>
            <div className="chart-container">
              <Bar data={barData} options={barOptions} />
            </div>
          </div>

          {/* Section 5: Cevap Bekleyen Mesajlar */}
          <div className="details-card">
            <div className="details-header">
              <h3 className="details-title">Cevap Bekleyen Mesajlar</h3>
              <Link to="/messages" style={{ color: '#60a5fa', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Tümünü Gör →</Link>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {[
                { name: 'Instagram', count: unreadMessagesByPlatform.instagram, bg: 'rgba(236, 72, 153, 0.12)', border: 'rgba(236, 72, 153, 0.2)', color: '#f472b6' },
                { name: 'WhatsApp', count: unreadMessagesByPlatform.whatsapp, bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.2)', color: '#34d399' },
                { name: 'Messenger', count: unreadMessagesByPlatform.messenger, bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa' },
                { name: 'TikTok', count: unreadMessagesByPlatform.tiktok, bg: 'rgba(255, 255, 255, 0.05)', border: 'rgba(255, 255, 255, 0.1)', color: '#d1d5db' },
                { name: 'SMS', count: unreadMessagesByPlatform.sms, bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24' }
              ].map((badge, idx) => (
                <div key={idx} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', background: badge.bg, border: `1px solid ${badge.border}`,
                  borderRadius: 999, color: badge.color, fontSize: 12, fontWeight: 600
                }}>
                  {badge.name} · <strong style={{ color: '#fff' }}>{badge.count}</strong>
                </div>
              ))}
            </div>
          </div>

          {/* Section 6: Para Kazandıracak Fırsatlar */}
          <div className="details-card">
            <div className="details-header">
              <h3 className="details-title">Para Kazandıracak Fırsatlar</h3>
              <Link to="/opportunities" style={{ color: '#60a5fa', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Tümünü Gör →</Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {opportunities.map(opp => (
                <div key={opp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.02)', borderRadius: 14 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                    {opp.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#fff', fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.label}</div>
                  </div>
                  <span style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#93c5fd', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                    {opp.count} kişi
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }

        @media (max-width: 1024px) {
          .kpi-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .kpi-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 20px;
          }
          .kpi-card {
            padding: 16px;
            border-radius: 16px;
          }
          .kpi-value {
            font-size: 24px;
          }
          .kpi-label {
            font-size: 12px;
            margin-bottom: 4px;
          }
          .kpi-icon {
            width: 36px;
            height: 36px;
          }
          .kpi-icon svg {
            width: 16px;
            height: 16px;
          }
          .details-card {
            padding: 16px;
            border-radius: 16px;
          }
          .chart-container {
            height: 180px;
          }
          .details-header {
            margin-bottom: 14px;
          }
          .details-title {
            font-size: 14px;
          }
          .activity-item {
            padding: 10px 12px;
          }
          .activity-text {
            font-size: 12px;
          }
        }

        @media (max-width: 480px) {
          .kpi-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
        }

        .kpi-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 20px;
          padding: 24px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          transition: all 0.3s ease;
        }

        .kpi-card:hover {
          transform: translateY(-2px);
          border-color: rgba(255, 255, 255, 0.1);
          background: rgba(255, 255, 255, 0.05);
        }

        .kpi-left {
          display: flex;
          flex-direction: column;
        }

        .kpi-label {
          font-size: 13px;
          font-weight: 500;
          color: #9ca3af;
          margin-bottom: 8px;
        }

        .kpi-value {
          font-size: 28px;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.2;
        }

        .kpi-trend {
          font-size: 12px;
          font-weight: 600;
          margin-top: 8px;
        }

        .trend-up {
          color: #10b981;
        }

        .kpi-languages {
          font-size: 10px;
          font-weight: 600;
          color: #10b981;
          margin-top: 8px;
          line-height: 1.5;
          max-width: 220px;
        }

        .kpi-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .kpi-icon svg {
          width: 20px;
          height: 20px;
        }

        .icon-blue { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
        .icon-orange { background: rgba(249, 115, 22, 0.1); color: #f97316; }
        .icon-violet { background: rgba(var(--secondary-rgb), 0.1); color: var(--secondary); }
        .icon-green { background: rgba(16, 185, 129, 0.1); color: #10b981; }
        .icon-pink { background: rgba(236, 72, 153, 0.1); color: #ec4899; }
        .icon-teal { background: rgba(20, 184, 166, 0.1); color: #14b8a6; }

        .details-row {
          display: grid;
          grid-template-columns: 1.2fr 0.8fr;
          gap: 20px;
        }

        @media (max-width: 900px) {
          .details-row {
            grid-template-columns: 1fr;
          }
        }

        .details-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 24px;
          padding: 24px;
          display: flex;
          flex-direction: column;
        }

        .details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .details-title {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: #ffffff;
        }

        .details-subtitle {
          font-size: 12px;
          color: #6b7280;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.01);
          border: 1px solid rgba(255, 255, 255, 0.02);
          border-radius: 16px;
        }

        .activity-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .dot-success { background: #10b981; }
        .dot-primary { background: #3b82f6; }
        .dot-warning { background: #f59e0b; }

        .activity-text {
          color: #ffffff;
          font-size: 13px;
          flex-grow: 1;
        }

        .activity-time {
          color: #6b7280;
          font-size: 12px;
        }

        .chart-container {
          height: 240px;
          position: relative;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
